import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { sendChat } from "../../lib/api";

const sid = (() => {
  let s = localStorage.getItem("bgm_chat_sid");
  if (!s) { s = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem("bgm_chat_sid", s); }
  return s;
})();

const GREETING = { role: "assistant", text: "Hi, I'm the BGM Wealth Assistant. Ask me about credit repair, our plans, or how to get started. How can I help?" };

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [messages, loading, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { reply } = await sendChat(sid, text);
      setMessages((m) => [...m, { role: "assistant", text: reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I hit a snag. Please try again or book a free consultation." }]);
    } finally {
      setLoading(false);
    }
  };

  const quick = ["What's included in each plan?", "How does credit repair work?", "I want the free checklist"];

  return (
    <>
      <button data-testid="chat-toggle" onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[900] flex h-14 w-14 items-center justify-center rounded-full bg-[#d4af37] text-[#050505] shadow-[0_10px_40px_rgba(212,175,55,0.5)] transition-transform duration-300 hover:scale-110">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="glass-solid gold-border-glow fixed bottom-24 right-6 z-[900] flex h-[520px] w-[92vw] max-w-sm flex-col overflow-hidden rounded-3xl"
            data-testid="chat-panel">
            <div className="flex items-center gap-3 border-b border-white/10 p-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#d4af37] font-display text-sm font-bold text-[#050505]">B</span>
              <div>
                <p className="font-display text-sm text-[#f5f5f5]">BGM Wealth Assistant</p>
                <p className="font-body text-[11px] text-gold">Online · replies instantly</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`chat-msg-${m.role}`}
                    className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 font-body text-sm ${m.role === "user" ? "bg-[#d4af37] text-[#050505]" : "bg-white/5 text-[#e6e6e6]"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start"><div className="rounded-2xl bg-white/5 px-4 py-3"><Loader2 className="h-4 w-4 animate-spin text-gold" /></div></div>
              )}
              {messages.length <= 1 && (
                <div className="space-y-2 pt-2">
                  {quick.map((q) => (
                    <button key={q} onClick={() => { setInput(q); setTimeout(() => send(), 30); }}
                      className="block w-full rounded-xl border border-white/10 px-3 py-2 text-left font-body text-xs text-[#c9c9c9] hover:border-[#d4af37]/50 hover:text-gold">{q}</button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={send} className="border-t border-white/10 p-3">
              <div className="flex items-center gap-2">
                <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your question…"
                  className="flex-1 rounded-full border border-white/10 bg-black/40 px-4 py-2.5 font-body text-sm text-[#f5f5f5] outline-none focus:border-[#d4af37]" />
                <button data-testid="chat-send" type="submit" disabled={loading}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-[#d4af37] text-[#050505] disabled:opacity-50">
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-2 text-center font-body text-[10px] text-[#666]">Educational info only. Results vary; no outcomes guaranteed.</p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
